import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { Apollo } from 'apollo-angular';
import { combineLatest, Observable, of } from 'rxjs';
import { distinctUntilChanged, map, switchMap, take, takeUntil } from 'rxjs/operators';
import { TcsNavigationService } from 'src/app/shared/tcs-navigation.service';
import { GetCardActiveAlertsByCardIdGQL } from 'src/graphql/generated/graphql.generated';
import { ActiveAlertViewModel } from 'src/models/active-alert-view-model';
import { BaseViewModel } from 'src/models/base-view-model';
import {
  CardSummaryViewModel,
  selectCardSummaryByTripId,
} from 'src/models/card-summary-view-model';
import { CardTimelineItemViewModel } from 'src/models/card-timeline-item-view-model';
import { CardViewModel } from 'src/models/card-view-model';
import { CommunicationCenterViewModel } from 'src/models/communication-center-view-model';
import { CompanyViewModel } from 'src/models/company-view-model';
import { ControlZoneViewModel } from 'src/models/control-zone-view-model';
import { FboViewModel } from 'src/models/fbo-view-model';
import { LocationViewModel, LocationViewModelService } from 'src/models/location-view-model';
import { MedicalFacilityViewModel } from 'src/models/medical-facility-view-model';
import { OperationViewModel } from 'src/models/operation-view-model';
import { PersonViewModel } from 'src/models/person-view-model';
import { RegionViewModel } from 'src/models/region-view-model';
import { RequestLocationViewModel } from 'src/models/request-location-view-model';
import { RequestPatientViewModel } from 'src/models/request-patient-view-model';
import { RequestViewModel } from 'src/models/request-view-model';
import { TimelineItemViewModel } from 'src/models/timeline-item-view-model';
import { TimezoneViewModelService } from 'src/models/timezone-view-model';
import { TransportOfferViewModel } from 'src/models/transport-offer-view-model';
import { UnitSummaryViewModel } from 'src/models/unit-summary-view-model';
import { UnitViewModel } from 'src/models/unit-view-model';
import { VehicleSummaryViewModel } from 'src/models/vehicle-summary-view-model';
import { VehicleViewModelService } from 'src/models/vehicle-view-model';
import { selectViewModel } from 'src/models/view-model-helper';
import { WaypointViewModelService } from 'src/models/waypoint-view-model';
import { AppState } from 'src/store/appstate';
import {
  selectCardByCardNumber,
  selectCardById,
  selectCardByOperationId,
  selectCardByRequestId,
} from 'src/store/entityStores/card-selectors';
import { selectCommunicationCenterById } from 'src/store/entityStores/communication-center-selectors';
import { selectCompanyByCompanyCode } from 'src/store/entityStores/company-selectors';
import { selectActiveOperationByUnitId } from 'src/store/entityStores/operation-selectors';
import {
  selectRegionByCardId,
  selectRegionByCardNumber,
  selectRegionById,
} from 'src/store/entityStores/region-selectors';
import {
  selectFirstTransportOfferByUnitId,
  selectTransportOfferById,
} from 'src/store/entityStores/transport-offer-selectors';
import {
  selectNonThirdPartyUnitByBaseId,
  selectUnitByCardNumber,
} from 'src/store/entityStores/unit-selectors';
import { QueryOptions } from 'src/store/fetch-services';
import { selectAccessibleItemEntitiesByPersonId } from 'src/store/generated/accessible-item-record.generated';
import { selectActiveAlertById } from 'src/store/generated/active-alert-record.generated';
import {
  CardRecord,
  selectCardEntitiesByRequestId,
} from 'src/store/generated/card-record.generated';
import {
  selectCardSummaryById,
  selectCardSummaryEntitiesByCardNumber,
  selectCardSummaryEntitiesByUnitId,
} from 'src/store/generated/card-summary-record.generated';
import {
  ActiveAlertEntityFetchService,
  BaseEntityFetchService,
  CardEntityFetchService,
  CompanyEntityFetchService,
  DiversionReasonEntityFetchService,
  FboEntityFetchService,
  LocationEntityFetchService,
  LocationHazardEntityFetchService,
  MedicalFacilityContactEntityFetchService,
  MedicalFacilityEntityFetchService,
  MedicalFacilityPhysicianEntityFetchService,
  MedicalFacilityUnitEntityFetchService,
  OperationEntityFetchService,
  OutOfServiceEventEntityFetchService,
  PersonEntityFetchService,
  PhysicianEntityFetchService,
  RequestEntityFetchService,
  TransferReasonEntityFetchService,
  UnitEntityFetchService,
} from 'src/store/generated/client-sync.generated';
import { selectAllCompanyEntities } from 'src/store/generated/company-record.generated';
import { ControlZoneRecord } from 'src/store/generated/control-zone-record.generated';
import { MedicalFacilityPhysicianRecord } from 'src/store/generated/medical-facility-physician-record.generated';
import { MedicalFacilityUnitRecord } from 'src/store/generated/medical-facility-unit-record.generated';
import { PhysicianRecord } from 'src/store/generated/physician-record.generated';
import { RegionProps, RegionRecord } from 'src/store/generated/region-record.generated';
import { selectRequestLocationById } from 'src/store/generated/request-location-record.generated';
import { selectRequestPatientById } from 'src/store/generated/request-patient-record.generated';
import { selectTimelineItemById } from 'src/store/generated/timeline-item-record.generated';
import { selectTimestampTypeById } from 'src/store/generated/timestamp-type-record.generated';
import { selectUnitSummaryById } from 'src/store/generated/unit-summary-record.generated';
import { selectVehicleSummaryById } from 'src/store/generated/vehicle-summary-record.generated';
import { selectQueryParam, selectRouteParam } from 'src/store/router/router-selectors';
import { undefinedOrMap, undefinedOrSwitchMap, valueOrSwitchMap } from 'src/util/rxjs/operators';
import { filterNull } from 'src/util/rxjs/operators/filter-null';
import { isNullOrEmpty } from 'src/util/string';
import {
  selectCardLinkEntitiesByFromCardId,
  selectCardLinkEntitiesByToCardId,
} from 'src/store/generated/card-link-record.generated';
import { CardLinkViewModel } from 'src/models/card-link-view-model';
import { CardLinkFetchService } from 'src/lib/tcs-card-link/card-link-fetch.service';
import { Seq } from 'immutable';
import { shareReplaySafe } from 'src/util/rxjs/operators/share-replay-safe';
import { OpsReadonlyService } from 'src/lib/tcs-ops-readonly/ops-readonly.service';
import { firstValueOrUndefined } from 'src/util/rxjs/observables/firstValue';
import { TickService } from './tick.service';

@Injectable({
  providedIn: 'root',
})
export class ViewModelService {
  private _companies$: Observable<CompanyViewModel[]>;

  constructor(
    public store: Store<AppState>,
    public readonly locations: LocationEntityFetchService,
    private readonly _waypoint: WaypointViewModelService,
    private readonly _vehicle: VehicleViewModelService,
    private readonly _locationViewModel: LocationViewModelService,
    public readonly opsReadonly: OpsReadonlyService,
    public readonly locationHazards: LocationHazardEntityFetchService,
    public readonly medicalFacilities: MedicalFacilityEntityFetchService,
    public readonly medicalFacilityContacts: MedicalFacilityContactEntityFetchService,
    public readonly medicalFacilityUnits: MedicalFacilityUnitEntityFetchService,
    public readonly medicalFacilityPhysicians: MedicalFacilityPhysicianEntityFetchService,
    public readonly physicians: PhysicianEntityFetchService,
    public readonly units: UnitEntityFetchService,
    public readonly companies: CompanyEntityFetchService,
    public readonly operations: OperationEntityFetchService,
    public readonly cards: CardEntityFetchService,
    public readonly requests: RequestEntityFetchService,
    public readonly people: PersonEntityFetchService,
    public readonly bases: BaseEntityFetchService,
    public readonly fbos: FboEntityFetchService,
    public readonly nav: TcsNavigationService,
    public readonly apollo: Apollo,
    public readonly ticker: TickService,
    public readonly timezoneService: TimezoneViewModelService,
    public readonly activeAlerts: ActiveAlertEntityFetchService,
    public readonly activeAlertsByCardId: GetCardActiveAlertsByCardIdGQL,
    public readonly oosEvents: OutOfServiceEventEntityFetchService,
    public readonly diversionReasons: DiversionReasonEntityFetchService,
    public readonly transferReasons: TransferReasonEntityFetchService,
    public readonly cardLinks: CardLinkFetchService,
  ) {
    this._companies$ = this._getAllCompanies().pipe(shareReplaySafe(1));
  }

  public getCardSummaryByCardNumber(
    cardNumber: string,
  ): Observable<CardSummaryViewModel | undefined> {
    return this.store.select(selectCardSummaryEntitiesByCardNumber(cardNumber)).pipe(
      map((cards) => {
        const card = cards.first(undefined);
        return card != null ? new CardSummaryViewModel(card, this.store) : undefined;
      }),
    );
  }

  public getCardSummaryByTripId(tripId: string): Observable<CardSummaryViewModel | undefined> {
    return this.store
      .select(selectCardSummaryByTripId(tripId))
      .pipe(
        map((cardSummary) =>
          cardSummary != null ? new CardSummaryViewModel(cardSummary, this.store) : undefined,
        ),
      );
  }

  public getCardSummaryById(id: string): Observable<CardSummaryViewModel | undefined> {
    return this.store.select(selectCardSummaryById(id)).pipe(
      map((card) => {
        return card != null ? new CardSummaryViewModel(card, this.store) : undefined;
      }),
    );
  }

  public getCardSummaryByUnitId(unitId: string): Observable<CardSummaryViewModel | undefined> {
    return this.store.select(selectCardSummaryEntitiesByUnitId(unitId)).pipe(
      map((cards) => {
        const card = cards.first(undefined);
        return card != null ? new CardSummaryViewModel(card, this.store) : undefined;
      }),
    );
  }

  public getUnitSummaryById(unitId: string): Observable<UnitSummaryViewModel | undefined> {
    return this.store
      .select(selectUnitSummaryById(unitId))
      .pipe(map((unit) => (unit != null ? new UnitSummaryViewModel(unit) : undefined)));
  }

  public getVehicleSummaryById(unitId: string): Observable<VehicleSummaryViewModel | undefined> {
    return this.store
      .select(selectVehicleSummaryById(unitId))
      .pipe(map((unit) => (unit != null ? new VehicleSummaryViewModel(unit) : undefined)));
  }
  public getCardByCardNumber(cardNumber: string): Observable<CardViewModel | undefined> {
    return this.selectViewModel(
      selectRegionByCardNumber(cardNumber),
      (r) => new RegionViewModel(r, this.store),
    ).pipe(
      switchMap((region) => {
        if (region == null) {
          return of(undefined);
        }
        return this.selectViewModel(
          selectCardByCardNumber(cardNumber),
          (c) => new CardViewModel(c, region, this),
        );
      }),
    );
  }

  public getCardLinksByFromCardId(cardId: string) {
    return this.selectViewModel(selectCardLinkEntitiesByFromCardId(cardId), (cardLinks) => {
      return cardLinks.map((cl) => new CardLinkViewModel(cl));
    }).pipe(map((cardLinks) => (cardLinks == null ? Seq.Indexed<CardLinkViewModel>() : cardLinks)));
  }

  public getCardLinksByToCardId(cardId: string, onDestroy: Observable<any>) {
    firstValueOrUndefined(this.cardLinks.fetchAllByToCardId(cardId).pipe(takeUntil(onDestroy)));
    return this.selectViewModel(selectCardLinkEntitiesByToCardId(cardId), (cardLinks) => {
      return cardLinks.map((cl) => new CardLinkViewModel(cl));
    }).pipe(map((cardLinks) => (cardLinks == null ? Seq.Indexed<CardLinkViewModel>() : cardLinks)));
  }

  public getBaseById(
    baseId: string,
    options: QueryOptions = {},
  ): Observable<BaseViewModel | undefined> {
    return this.bases.getById(baseId, options).pipe(
      undefinedOrSwitchMap((base) => {
        const company$ = this.getCompanyById(base.companyId, options).pipe(filterNull());
        const aviationCompany$ =
          base.aviationCompanyId != null
            ? this.getCompanyById(base.aviationCompanyId, options)
            : of(undefined);
        const dispatchBase$ =
          base.dispatchBaseId != null && base.dispatchBaseId !== base.id
            ? this.getBaseById(base.dispatchBaseId, options)
            : of(undefined);
        const reportBase$ =
          base.reportBaseId != null && base.reportBaseId !== base.id
            ? this.bases.getById(base.reportBaseId, options).pipe(
                undefinedOrSwitchMap((reportBaseEntity) => {
                  const reportBaseCompany$ = this.getCompanyById(
                    reportBaseEntity.companyId,
                    options,
                  ).pipe(filterNull());
                  const reportBaseLocation$ = this.getLocationById_deprecated(
                    reportBaseEntity.locationId,
                    options,
                  ).pipe(filterNull());
                  return combineLatest([reportBaseCompany$, reportBaseLocation$]).pipe(
                    map(([reportBaseCompany, reportBaseLocation]) => {
                      return new BaseViewModel(
                        reportBaseEntity,
                        reportBaseLocation,
                        reportBaseCompany,
                        undefined,
                        undefined,
                        undefined,
                        this,
                      );
                    }),
                  );
                }),
              )
            : of(undefined);
        const location$ = this.getLocationById_deprecated(base.locationId, options).pipe(
          filterNull(),
        );
        return combineLatest([
          company$,
          aviationCompany$,
          dispatchBase$,
          reportBase$,
          location$,
        ]).pipe(
          map(([company, aviationCompany, dispatchBase, reportBase, location]) => {
            return new BaseViewModel(
              base,
              location,
              company,
              aviationCompany,
              dispatchBase,
              reportBase,
              this,
            );
          }),
        );
      }),
    );
  }

  public getCardById(cardId: string): Observable<CardViewModel | undefined> {
    return this.selectViewModel(
      selectRegionByCardId(cardId),
      (r) => new RegionViewModel(r, this.store),
    ).pipe(
      switchMap((region) =>
        region == null
          ? of(undefined)
          : this.selectViewModel(selectCardById(cardId), (c) => new CardViewModel(c, region, this)),
      ),
    );
  }

  public getCardByRequestId(requestId: string): Observable<CardViewModel | undefined> {
    return this.store.select(selectCardByRequestId(requestId)).pipe(
      filterNull(),
      switchMap((c) => {
        return this.selectViewModel(
          selectRegionByCardId(c.id),
          (r) => new RegionViewModel(r, this.store),
        ).pipe(
          switchMap((region) =>
            region == null
              ? of(undefined)
              : this.selectViewModel(
                  selectCardById(c.id),
                  (card) => new CardViewModel(card, region, this),
                ),
          ),
        );
      }),
    );
  }

  public getCurrentCard() {
    return this.getRouteParam('cardNumber').pipe(
      distinctUntilChanged(),
      switchMap((cardNumber) =>
        cardNumber == null ? of(undefined) : this.getCardByCardNumber(cardNumber),
      ),
    );
  }

  public getCurrentRegion() {
    return this.getRouteParam('regionId').pipe(
      valueOrSwitchMap(() =>
        this.getRouteParam('cardNumber').pipe(
          undefinedOrSwitchMap((cardNumber) =>
            this.store.select(selectRegionByCardNumber(cardNumber)),
          ),
          undefinedOrMap((region) => region.id),
        ),
      ),
      distinctUntilChanged(),
      undefinedOrSwitchMap((regionId) => this.getRegionById(regionId)),
    );
  }

  public getRegionById(regionId: string): Observable<RegionViewModel | undefined> {
    return this.selectViewModel(selectRegionById(regionId), (r) => this._region(r));
  }

  public getQueryParam(queryParamName: string): Observable<string | undefined> {
    return this.store.select(selectQueryParam(queryParamName));
  }

  public getRouteParam(paramName: string): Observable<string | undefined> {
    return this.store.select(selectRouteParam(paramName));
  }

  private _region(region: RegionRecord) {
    return new RegionViewModel(region, this.store);
  }

  private _controlZone(controlZone: ControlZoneRecord) {
    return new ControlZoneViewModel(controlZone, this.store);
  }

  public getUnitForCard(
    cardNumber: string,
    options?: QueryOptions,
  ): Observable<UnitViewModel | undefined> {
    return this.store.select(selectUnitByCardNumber(cardNumber)).pipe(
      distinctUntilChanged(),
      undefinedOrSwitchMap((u) => this.getUnitById(u.id, options)),
    );
  }

  public getUnitFromRouteParamIfExists(
    idParamName: string = 'id',
    options?: QueryOptions,
  ): Observable<UnitViewModel> {
    return this.store.select(selectRouteParam(idParamName)).pipe(
      switchMap((param) => {
        return param != null
          ? this.getUnitById(param, options)
          : of(undefined as UnitViewModel | undefined);
      }),
      filterNull(),
    );
  }

  public getUnitById(
    unitId: string,
    options?: QueryOptions,
  ): Observable<UnitViewModel | undefined> {
    return this.units.getById(unitId, options).pipe(
      undefinedOrSwitchMap((u) => {
        const region$ = this.getRegionById(u.regionId);
        const vehicle$ =
          u.vehicleId == null ? of(undefined) : this.getVehicle(u.vehicleId, options);
        return combineLatest([region$, vehicle$]).pipe(
          map(([region, vehicle]) => {
            return region == null ? undefined : new UnitViewModel(u, region, vehicle, this);
          }),
        );
      }),
    );
  }

  public getVehicle(vehicleId: string, options?: QueryOptions) {
    return this._vehicle.getById(vehicleId, options);
  }

  public getNonThirdPartyUnitByBaseId(baseId: string) {
    return this.store
      .select(selectNonThirdPartyUnitByBaseId(baseId))
      .pipe(undefinedOrSwitchMap((u) => this.getActiveCardForUnit(u.id)));
  }

  public getPersonById(
    personId: string | null,
    options?: QueryOptions,
  ): Observable<PersonViewModel | undefined> {
    if (personId == null) {
      return of(undefined);
    }
    return this.people
      .getById(personId, options)
      .pipe(
        undefinedOrSwitchMap((person) =>
          this.store
            .select(selectAccessibleItemEntitiesByPersonId(personId))
            .pipe(map((ai) => new PersonViewModel(person, ai.toArray()))),
        ),
      );
  }

  public getActiveCardForUnit(unitId: string): Observable<CardViewModel | undefined> {
    const activeCard$: Observable<CardRecord | undefined> = this.store
      .select(selectActiveOperationByUnitId(unitId))
      .pipe(
        switchMap((operation) => {
          if (operation != null) {
            return this.store.select(selectCardByOperationId(operation.id));
          }
          return of(undefined as CardRecord | undefined);
        }),
      );

    return activeCard$.pipe(
      switchMap((card) => {
        if (card != null) {
          return this.getCardByCardNumber(card.cardNumber);
        }
        return of(undefined as CardViewModel | undefined);
      }),
    );
  }

  public getOperationById(
    operationId: string,
    options?: QueryOptions,
  ): Observable<OperationViewModel | undefined> {
    return this.operations.getById(operationId, options).pipe(
      undefinedOrSwitchMap((operation) => {
        const card$ = this.store
          .select(selectCardByOperationId(operationId))
          .pipe(undefinedOrSwitchMap((card) => this.getCardById(card.id)));
        const unit$ =
          operation.unitId != null ? this.getUnitById(operation.unitId, options) : of(undefined);
        return combineLatest([card$, unit$]).pipe(
          map(([card, unit]) => {
            return card == null ? undefined : new OperationViewModel(operation, card, unit, this);
          }),
        );
      }),
    );
  }

  public getRequestById(
    requestId: string,
    options?: QueryOptions,
  ): Observable<RequestViewModel | undefined> {
    return combineLatest([
      this.store.select(selectCardEntitiesByRequestId(requestId)),
      this.requests.getById(requestId, options),
    ]).pipe(
      switchMap(([cards, request]) => {
        const c = cards.first(undefined);
        if (c != null && request != null) {
          return this.getCardById(c.id).pipe(
            undefinedOrMap((cvm) => new RequestViewModel(request, cvm, this)),
          );
        }
        return of(undefined);
      }),
    );
  }

  public getTransportOfferById(
    transportOfferId: string,
    options?: QueryOptions,
  ): Observable<TransportOfferViewModel | undefined> {
    return this.store.select(selectTransportOfferById(transportOfferId)).pipe(
      switchMap((offer) =>
        combineLatest([
          of(offer),
          offer?.unitId != null ? this.getUnitSummaryById(offer.unitId) : of(undefined),
          this._companies$,
        ]),
      ),
      switchMap(([offer, unit, companies]) => {
        const company = unit == null ? undefined : companies.find((c) => c.id === unit.companyId);
        return offer == null
          ? of(undefined)
          : this.getRequestById(offer.requestId, options).pipe(
              undefinedOrMap((req) => new TransportOfferViewModel(offer, req, company, this)),
            );
      }),
    );
  }

  public getFirstTransportOfferByUnitId(
    unitId: string,
    excludeTransportOfferId: string,
    options?: QueryOptions,
  ) {
    return this.store
      .select(selectFirstTransportOfferByUnitId(unitId, excludeTransportOfferId))
      .pipe(
        switchMap((offer) =>
          combineLatest([
            of(offer),
            offer?.unitId != null ? this.getUnitSummaryById(offer.unitId) : of(undefined),
            this._companies$,
          ]),
        ),
        switchMap(([offer, unit, companies]) => {
          const company = unit == null ? undefined : companies.find((c) => c.id === unit.companyId);
          return offer == null
            ? of(undefined)
            : this.getRequestById(offer.requestId, options).pipe(
                undefinedOrMap((req) => new TransportOfferViewModel(offer, req, company, this)),
              );
        }),
      );
  }

  public getRequestLocationById(
    requestLocationId: string,
    options?: QueryOptions,
  ): Observable<RequestLocationViewModel | undefined> {
    return this.store.select(selectRequestLocationById(requestLocationId)).pipe(
      distinctUntilChanged(),
      undefinedOrSwitchMap((rl) => {
        const request$ = this.getRequestById(rl.requestId, options).pipe(take(1));
        const location$ =
          rl.locationId != null
            ? this.getLocationById_deprecated(rl.locationId, options).pipe(take(1))
            : of(undefined);
        return request$.pipe(
          undefinedOrSwitchMap((r) => {
            return location$.pipe(map((l) => new RequestLocationViewModel(rl, r, l, this)));
          }),
        );
      }),
    );
  }

  public getLocationById_deprecated(
    locationId: string,
    options?: QueryOptions,
  ): Observable<LocationViewModel | undefined> {
    return this._locationViewModel.getById(locationId, options);
  }

  public getCompanyByCode(companyCode: string, options: QueryOptions = {}) {
    return this.store
      .select(selectCompanyByCompanyCode(companyCode))
      .pipe(undefinedOrSwitchMap((company) => this.getCompanyById(company.id, options)));
  }

  public getCompanyById(
    companyId: string,
    options: QueryOptions,
  ): Observable<CompanyViewModel | undefined> {
    return this.companies
      .getById(companyId, options)
      .pipe(undefinedOrMap((c) => new CompanyViewModel(c, this)));
  }

  public getAllCompanies(): Observable<CompanyViewModel[]> {
    return this._companies$;
  }
  private _getAllCompanies(): Observable<CompanyViewModel[]> {
    return this.store.select(selectAllCompanyEntities).pipe(
      map((companies) =>
        companies
          .valueSeq()
          .map((c) => new CompanyViewModel(c, this))
          .toArray(),
      ),
    );
  }

  public getCurrentCompany(): Observable<CompanyViewModel | undefined> {
    return this.store.select(selectRouteParam('companyCode')).pipe(
      switchMap((companyCode) => {
        if (companyCode == null) {
          return of(undefined);
        }
        return this.selectViewModel(
          selectCompanyByCompanyCode(companyCode),
          (company) => new CompanyViewModel(company, this),
        );
      }),
    );
  }

  public getCurrentCommunicationCenter(region: RegionProps | undefined) {
    return combineLatest([this.getQueryParam('communicationCenterId'), of(region)]).pipe(
      distinctUntilChanged(),
      map(([commCenterId, rgn]) => {
        const ccId = !isNullOrEmpty(commCenterId)
          ? commCenterId
          : rgn != null
          ? rgn.communicationCenterId
          : null;

        return ccId;
      }),
      distinctUntilChanged(),
      switchMap((commCenterId) =>
        commCenterId == null ? of(undefined) : this.getCommunicationCenterById(commCenterId),
      ),
    );
  }

  public getCommunicationCenterById(
    commCenterId: string,
  ): Observable<CommunicationCenterViewModel | undefined> {
    return this.selectViewModel(
      selectCommunicationCenterById(commCenterId),
      (commCenter) => new CommunicationCenterViewModel(commCenter, this.store),
    );
  }

  private selectViewModel<TIn, TOut>(
    mapFn: (state: AppState) => TIn | undefined,
    createViewModelFn: (v: TIn) => TOut,
  ) {
    return selectViewModel(this.store, mapFn, createViewModelFn);
  }

  public getMedicalFacilityById(
    medicalFacilityId: string,
    options?: QueryOptions,
  ): Observable<MedicalFacilityViewModel | undefined> {
    return this.medicalFacilities.getById(medicalFacilityId, options).pipe(
      switchMap((mf) => {
        if (mf == null) {
          return of(undefined);
        }
        if (mf.locationId != null) {
          return this.getLocationById_deprecated(medicalFacilityId, options).pipe(
            map((loc) => new MedicalFacilityViewModel(mf, loc, this)),
          );
        }
        return of(new MedicalFacilityViewModel(mf, undefined, this));
      }),
    );
  }

  public getMedicalFacilityUnitById(
    medicalFacilityUnitId: string,
    options?: QueryOptions,
  ): Observable<MedicalFacilityUnitRecord | undefined> {
    return this.medicalFacilityUnits.getById(medicalFacilityUnitId, options).pipe(
      switchMap((mfu) => {
        if (mfu == null) {
          return of(undefined);
        }
        return of(mfu);
      }),
    );
  }

  public getMedicalFacilityPhysicianById(
    medicalFacilityPhysicianId: string,
    options?: QueryOptions,
  ): Observable<MedicalFacilityPhysicianRecord | undefined> {
    return this.medicalFacilityPhysicians.getById(medicalFacilityPhysicianId, options).pipe(
      switchMap((mfp) => {
        if (mfp == null) {
          return of(undefined);
        }
        return of(mfp);
      }),
    );
  }

  public getPhysicianById(
    physicianId: string,
    options?: QueryOptions,
  ): Observable<PhysicianRecord | undefined> {
    return this.physicians.getById(physicianId, options).pipe(
      switchMap((p) => {
        if (p == null) {
          return of(undefined);
        }
        return of(p);
      }),
    );
  }

  public getRequestPatientById(
    requestPatientId: string,
    options?: QueryOptions,
  ): Observable<RequestPatientViewModel | undefined> {
    return this.store
      .select(selectRequestPatientById(requestPatientId))
      .pipe(
        undefinedOrSwitchMap((p) =>
          this.getRequestById(p.requestId, options).pipe(
            undefinedOrMap((r) => new RequestPatientViewModel(p, r, this)),
          ),
        ),
      );
  }

  public getWaypointById(waypointId: string, options?: QueryOptions) {
    return this._waypoint.getById(waypointId, options);
  }

  public getTimelineItemViewModel(
    timelineItemId: string,
  ): Observable<TimelineItemViewModel | undefined> {
    return this.store
      .select(selectTimelineItemById(timelineItemId))
      .pipe(
        switchMap((tl) =>
          tl == null
            ? of(undefined as TimelineItemViewModel | undefined)
            : this.store
                .select(selectTimestampTypeById(tl.timestampTypeId))
                .pipe(
                  map((tt) => (tt == null ? undefined : new TimelineItemViewModel(tl, tt, this))),
                ),
        ),
      );
  }

  public getCardTimelineItemViewModel(
    timelineItemId: string,
    timestampId: string | undefined,
    card: CardViewModel,
  ): Observable<CardTimelineItemViewModel | undefined> {
    return of(new CardTimelineItemViewModel(card, timelineItemId, timestampId, this, this.store));
  }

  public getActiveAlertById(activeAlertId: string) {
    return selectViewModel(
      this.store,
      selectActiveAlertById(activeAlertId),
      (activeAlert) => new ActiveAlertViewModel(activeAlert, this, undefined),
    );
  }

  public getFboById(id: string, options?: QueryOptions): Observable<FboViewModel | undefined> {
    return this.fbos.getById(id, options).pipe(
      undefinedOrSwitchMap((fbo) => {
        const location$ =
          fbo.locationId != null
            ? this._locationViewModel.getById(fbo.locationId, options)
            : of(undefined);

        return location$.pipe(map((location) => new FboViewModel(fbo, location)));
      }),
    );
  }
}
